import { useState, type ChangeEvent, type CSSProperties } from "react";
import { TextArea } from "@design-system/TextArea/TextArea";
import type {
    TextAreaForcedState,
    TextAreaSize,
} from "@design-system/TextArea/TextArea.types";
import {
    TextAreaPlaygroundControls,
    type TextAreaFeatureFlags,
} from "./TextAreaPlaygroundControls";
import { TextAreaMatrix } from "./TextAreaPlaygroundMatrix";
import {
    useAnatomyMode,
    type AnatomyForcedState,
} from "./componentAnatomyMode";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

/** TextArea has no pressed state, so that one state drops on the way through. */
const toTextAreaForcedState = (
    state: AnatomyForcedState | undefined,
): TextAreaForcedState | undefined => {
    switch (state) {
        case "hover":
        case "focus":
            return state;
        default:
            return undefined;
    }
};

/**
 * @purpose Sandbox for the Figma multiline field. Auto-grow and resizable stay
 * mutually exclusive here, matching the component's height-mode union, and the
 * stage is the 320px sample width.
 */

const stageStyle: CSSProperties = { width: 320 };

export const TextAreaPlayground = () => {
    const [size, setSize] = useState<TextAreaSize>("md");
    const [features, setFeatures] = useState<TextAreaFeatureFlags>({
        isInvalid: false,
        isDisabled: false,
        isReadOnly: false,
        isAutoGrow: false,
        isResizable: false,
    });
    const [value, setValue] = useState("Text area");

    const { isInvalid, isDisabled, isReadOnly, isAutoGrow, isResizable } =
        features;

    const anatomy = useAnatomyMode({
        component: "textArea",
        variantId: isInvalid && !isDisabled ? "primary-invalid" : "primary",
        sizeId: size,
        isDisabled,
        isReadOnly,
        textAreaContent: {
            text: value,
            placeholder: "Placeholder",
            isResizable: isResizable && !isDisabled && !isReadOnly,
        },
    });
    const forcedState = toTextAreaForcedState(anatomy.forcedState);

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setValue(event.target.value);
    };

    // Everything but the height mode; the mode props stay per branch so the
    // TextAreaHeightProps union keeps narrowing.
    const stageProps = {
        size,
        invalid: isInvalid,
        disabled: isDisabled,
        readOnly: isReadOnly,
        value,
        placeholder: "Placeholder",
        "data-forced-state": forcedState,
        onChange: handleChange,
    };

    const renderStage = () => {
        if (isAutoGrow) {
            return (
                <TextArea {...stageProps} autoGrow minRows={2} maxRows={5} />
            );
        }

        if (isResizable) {
            return <TextArea {...stageProps} resizable rows={3} />;
        }

        return <TextArea {...stageProps} rows={3} />;
    };

    const summary = `primary · ${size}`;
    const xraySummary = `x-ray · ${isInvalid ? "primary-invalid" : "primary"} · ${size}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <TextAreaPlaygroundControls
                        size={size}
                        onSizeChange={setSize}
                        value={value}
                        onValueChange={setValue}
                        features={features}
                        onFeaturesChange={setFeatures}
                    />
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? xraySummary : summary}
                belowStage={<TextAreaMatrix />}
                output={JSON.stringify(
                    {
                        size,
                        invalid: isInvalid,
                        disabled: isDisabled,
                        readOnly: isReadOnly,
                        autoGrow: isAutoGrow,
                        resizable: isResizable,
                        value,
                        forcedState: forcedState ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? <div style={stageStyle}>{renderStage()}</div>}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
